import * as angular from 'angular';
import * as _ from 'lodash';

import {
    Application,
    ApplicationUserViewModel,
    BulkEditProperty,
    ComponentDefinition,
    ConnectionPoint,
    ConnectionPointViewModel,
    Dictionary,
    DigitalSet,
    Group,
    GroupConfigMode,
    GroupTreeViewModel,
    IApplicationConfiguration,
    IApprovalStrategy,
    IStringDateRange2,
    IValueViewModel,
    Identifier,
    ImportResult,
    ObjectUser,
    State,
    TagDefinition,
    Template,
    TimeUnit,
    ValidationPageConfiguration,
    ValidationScope,
} from '../../../models';
import { WidgetContainerBase } from '../..';
import { IRootScope } from '../../../IRootScope';
import {
    IAuthorizationService,
    IGroupService,
    ILoggerService,
    IIdentifierStateService,
    ICurrentApplicationService,
    IStoreService,
    ISaveMessagesService,
    IModelWidgetService,
    IDateRangeService,
    IExportToCsvService,
    ITemplateService,
} from '../../../services';
import { IModelStructureMenu, IModelStructureService } from '../../services.index';
import { StoreElement, Store, TemplateCreationProperties } from '../../../stateManagement';
import {
    groupAdded,
    groupSelected,
    groupProperties,
    toggleEditMode,
    groupCanSelect,
    groupTreeChanged,
    identifierSelected,
    identifierSavedForce,
    identifierSaved,
    tagSelected,
    treeUpdated,
    guidEmpty,
    tagSaved,
    templateCreated,
    connectionPointTypes
} from '../../../common';

interface IModels {
    selectedGroup: Group;
    canEdit: boolean;
}

class ModelsStateController extends WidgetContainerBase implements IModels {
    appConfig: IApplicationConfiguration;
    applications: Application[];
    approvalStrategiesDictionary: Dictionary<IApprovalStrategy>;
    beginDate: string;
    bulkEditProperties: BulkEditProperty[];
    canEdit: boolean;
    canDelete: boolean;
    connectionPoints: ConnectionPoint[];
    connectionPointsDictionary: Dictionary<ConnectionPointViewModel>;
    currentState: string;
    digitalSets: DigitalSet[];
    endDate: string;
    errorMessage = '';
    filteredGroups: Group[];
    formController: angular.IFormController;
    groups: Group[];
    groupsDictionary: Dictionary<Group>;
    hasError: boolean;
    hiddenStates: string[];
    identifiers: Identifier[] = [];
    initialRange: IStringDateRange2;
    isAsyncStoreLoading: boolean;
    isCreateChildApplicationAllowed: boolean = false;
    isEditMode: boolean;
    isLoading: boolean;
    isStoreLoaded: boolean;
    loadingMessage: string;
    menu: IModelStructureMenu[];
    selectedGroup: Group;
    selectedIdentifier: Identifier;
    selectedItemType = 0;
    selectedTag: TagDefinition;
    selectedVM: IValueViewModel; // used in substate
    shared: any;
    statesDictionary: Dictionary<State>;
    tags: TagDefinition[];
    templates: Template[];
    templatesDictionary: Dictionary<Template>;
    usages: ObjectUser[];
    userViewModels: Dictionary<ApplicationUserViewModel>;
    validationPageConfigurations: { [name: string]: string };
    validationPageConfigurationsDictionary: Dictionary<ValidationPageConfiguration>;
    viewModelsDictionary: Dictionary<IValueViewModel>;

    readonly sharedState: any;

    private _unsubscribe: () => void;

    static $inject = [
        '$scope',
        '$rootScope',
        '$state',
        '$translate',
        '$q',
        'authorizationService',
        'currentApplicationService',
        'groupService',
        'identifierStateService',
        'translatedLoggerService',
        'modelWidgetService',
        'modelStructureService',
        'saveMessagesService',
        'dateRangeService',
        'storeService',
        'exportToCsvService',
        'templateService'
    ];

    constructor(
        private _$scope: angular.IScope,
        private _$rootScope: IRootScope,
        private _$state: angular.ui.IStateService,
        private _$translate: angular.translate.ITranslateService,
        private _$q: angular.IQService,
        private _authorizationService: IAuthorizationService,
        private _currentApplicationService: ICurrentApplicationService,
        private _groupService: IGroupService,
        private _identifierStateService: IIdentifierStateService,
        private _loggerService: ILoggerService,
        private _modelWidgetService: IModelWidgetService,
        private _modelStructureService: IModelStructureService,
        private _saveMessagesService: ISaveMessagesService,
        private _dateRangeService: IDateRangeService,
        private _storeService: IStoreService,
        private _exportToCsvService: IExportToCsvService,
        private _templateService: ITemplateService
    ) {
        super();
    }

    $onInit() {
        this._dateRangeService.initByState(this._$state.params);
        this.initialRange = {
            beginDate: this._dateRangeService.beginDate() as string,
            endDate: this._dateRangeService.endDate() as string
        };
        this.checkCreateChildApplicationAllowed();
        this.isEditMode = this._$rootScope.isEditMode;
        this.shared = this.sharedState;
        this.canEdit = this._authorizationService.canEdit();
        this.bulkEditProperties = _.sortBy(groupProperties, d => d.value);
        this.refreshTree(this.getGroupGuid());

        this._$scope.$on(groupAdded, (event, data) => {
            if (data && !data.isCreating) {
                this.refreshTree(this.getGroupGuid())
                    .then(() => {
                        this.selectedGroup = _.find(this.groups, g => g.guid === data.guid);
                        this._$rootScope.$broadcast(groupSelected, this.selectedGroup, true);
                    });
            }
        });

        this._$scope.$on(toggleEditMode, (event, data) => {
            this.isEditMode = !this.isEditMode;
        });

        this._$scope.$on(groupTreeChanged, (event) => {
            this.refreshTree(this.getGroupGuid());
        });

        this._$scope.$on(groupCanSelect, (event) => {
            if (!this.canSelectItem()) {
                event.preventDefault();
                this._identifierStateService.issueSelectionWarning();
            }
        });

        this._$scope.$on(groupSelected, (event, data, doReset: boolean = true) => {
            if (doReset) {
                this.selectedItemType = 0;
            }
            if (data) {
                this.selectedGroup = Object.assign({}, data, { parent: null, children: null, referenceGroup: data?.type ? this.validationPageConfigurations[data.type] : null });
            } else {
                this.selectedGroup = null;
            }
        });

        this._$scope.$on(identifierSelected, (event, data: Identifier) => {
            if (!this.canSelectItem()) {
                event.preventDefault();
                this._identifierStateService.issueSelectionWarning();
            } else {
                this.selectIdentifier(data);
            }
        });

        this._$scope.$on(identifierSavedForce, (event, data) => {
            this.refreshTree(this.getGroupGuid())
                .then(() => this.selectIdentifier(data));
        });

        this._$scope.$on(identifierSaved, (event, data) => {
            this.refreshTree(this.getGroupGuid())
                .then(() => this.selectIdentifier(data));
        });

        this._$scope.$on(tagSelected, (event: angular.IAngularEvent, data: TagDefinition) => {
            if (!this.canSelectItem()) {
                event.preventDefault();
                this._identifierStateService.issueSelectionWarning();
            } else {
                this.selectedItemType = 2;
                this.selectedTag = data;
            }
        });

        this._$scope.$on('$stateChangeSuccess', (event, toState) => {
            if (toState.name === 'models.structure') {
                this.refreshTree(this.getGroupGuid());
            }
            this.currentState = this._$state.current.name;
        });

        this.currentState = this._$state.current.name;
        this.menu = this._modelStructureService.getMenu();

        this.isAsyncStoreLoading = true;
        this._unsubscribe = this._storeService.registerAsync({
            callback: store => this.refreshStore(store, false),
            elements: [
                StoreElement.ApprovalStrategies,
                StoreElement.ConnectionPoints,
                StoreElement.DigitalSets,
                StoreElement.States,
                StoreElement.Tags,
                StoreElement.Templates,
                StoreElement.Users,
                StoreElement.ValidationPageConfigurations
            ]
        }, {
            callback: store => this.refreshStore(store, true),
            elements: [
                StoreElement.Alarms,
                StoreElement.ApprovalStrategies,
                StoreElement.Groups,
                StoreElement.Tags,
                StoreElement.Templates,
                StoreElement.ValueViewModels
            ]
        });
    }

    $onDestroy() {
        if (this._unsubscribe) {
            this._unsubscribe();
        }
    }

    unselect($event: any) {
        if ((!this.formController || !this.formController.$dirty) && !$event.isDefaultPrevented()) {
            this.reselect(null);
        }
    }

    // templates management
    createTemplate(config: TemplateCreationProperties): angular.IPromise<Template> {
        this.isLoading = true;
        let template: Template;
        return this._storeService.dispatchers().templates
            .create(config)
            .then(data => {
                template = data;
                return this._storeService.dispatchers().groups.getOne(config.groupGuid);
            })
            .then(group => {
                this.refreshTree(config.groupGuid);
                this._loggerService.logSuccess('Template created');
                this._$rootScope.$broadcast(templateCreated);
                return template;
            })
            .catch(reason => {
                this._loggerService.logError('Cannot create template', true, reason);
                throw reason;
            })
            .finally(() => this.isLoading = false);
    }

    deleteTemplate(guid: string): angular.IPromise<Template[]> {
        this.isLoading = true;
        return this._storeService.dispatchers().templates
            .deleteOne(guid)
            .then(data => {
                this._loggerService.logSuccess('Successfully deleted template');
                return data;
            })
            .catch(reason => {
                this._loggerService.logError('Failed to delete template');
                throw reason;
            })
            .finally(() => this.isLoading = false);
    }


    deleteItem(guid: string): void {
        this._storeService.dispatchers().groups
            .delete(guid)
            .then(data => {
                this._loggerService.logSuccess('Group successfully deleted.', true);
                this.reselect(null);
                return this.refreshTree(this.getGroupGuid());
            })
            .catch(reason => {
                this._loggerService.logError('Failed to delete group.', true);
            });
    }

    stopPropagation(event: angular.IAngularEvent): void {
        event.stopPropagation();
    }

    users(selectedGroup: any) {
        return selectedGroup && selectedGroup.users
            ? _.filter(selectedGroup.users,
                (d: any) => d.guid !== selectedGroup.guid && d.ownerGuid !== selectedGroup.guid)
            : [];
    }

    getVisibleGroupWithScript(): Group[] {
        return (this.filteredGroups ? this.filteredGroups.filter(g => g.script) : []);
    }

    computeVisibleMatrix() {
        this.isLoading = true;
        const guids = this.filteredGroups.map(g => g.guid).filter(g => g !== guidEmpty);
        const promise = this._storeService.dispatchers().groups.updateSomeOccurenceMatrix(guids);
        return this.computeMatricesFeedback(promise);
    }

    computeErrorMatrix() {
        this.isLoading = true;
        const promise = this._storeService.dispatchers().groups.updateAllOccurenceMatrix();
        return this.computeMatricesFeedback(promise);
    }

    onBulkEdit(data: number) {
        this._loggerService.logSuccess(`${data} properties updated.`);
        this._groupService.cleanGroupCache();
        this.refreshTree(this.getGroupGuid());
    }

    checkDeleteGroup(groupGuid: string) {
        this.usages = [];
        if (groupGuid) {
            this.isLoading = true;
            this.selectedGroup = _.find(this.groups, group => group.guid === groupGuid);
            this._groupService
                .getUsagesForGroup(groupGuid)
                .then(data => {
                    this.usages = data;
                    this.canDelete = this._groupService.canDeleteGroup(groupGuid, data, this.groupsDictionary);
                    if (data && data.length > 0) {
                        // Can't delete because group is already in use
                        $('#deleteGroupModal').modal('show');
                    } else {
                        // Can delete group, display confirmation modal
                        $('#deleteItemModal').modal('show');
                    }
                })
                .catch(error => this._loggerService.logError(error))
                .finally(() => this.isLoading = false);
        }
    }

    saveTag() {
        const promise = this._storeService.dispatchers().tags.update([this.selectedTag]);
        return this._saveMessagesService
            .saveTag(promise)
            .then(tags => this._$rootScope.$broadcast(tagSaved, tags));
    }

    onGroupCancel() {
        if (this.selectedGroup.isCreating) {
            this.groups = this.groups.filter(g => g.guid !== this.selectedGroup.guid);
            this.refreshTree(null);
        }
        this.selectedGroup = null;
    }

    exportToCsv(): void {
        const dataTable: any[][] = [];

        const headers = [
            'guid',
            'Name',
            'Unique name',
            'Type',
            'Short comment',
            'Long comment',
            'Template name',
            'Frequency',
            'Frequency time unit',
            'Validation mode',
            'Validation scope',
        ].concat(this._exportToCsvService.getUserFieldsHeaders(this.filteredGroups));
        dataTable.push(headers);

        this.filteredGroups.forEach(group => {
            let row: any[] = [];

            const template = this.templatesDictionary[group.templateGuid];

            row.push(group.guid);
            row.push(group.name);
            row.push(group.uniqueName);
            row.push(group.type);
            row.push(group.shortComment);
            row.push(group.longComment);
            row.push(template?.name ?? '');
            row.push(group.frequency.interval);
            row.push(group.frequency.intervalTimeUnit !== undefined ? TimeUnit[group.frequency.intervalTimeUnit] : '');
            row.push(group.configMode !== undefined ? GroupConfigMode[group.configMode] : '');
            row.push(group.validationScope !== undefined ? ValidationScope[group.validationScope] : ValidationScope[ValidationScope.SinglePeriod]);

            if (group.customProperties && Object.keys(group.customProperties).length > 0) {
                row = row.concat(this._exportToCsvService.getUserFieldsValues(group.customProperties));
            }

            dataTable.push(row);
        });
        this._exportToCsvService.getCsvFromData(dataTable, `${this.appConfig.name}.csv`);
    }

    importCsv(file: File): angular.IPromise<ImportResult> {
        return this._groupService
            .upload(file)
            .then(data => {
                this._storeService.dispatchers().groups.getAll(true);
                this._storeService.dispatchers().valueViewModels.getAll(true);
                return data;
            });
    }

    private getGroupGuid(): string {
        let groupGuid = null;
        if (this._$state.params['groupGuid']) {
            groupGuid = this._$state.params['groupGuid'];
        }
        if (!groupGuid && this.selectedGroup && this.selectedGroup.guid) {
            groupGuid = this.selectedGroup.guid;
        }
        return groupGuid;
    }

    private reselect(group: Group) {
        this.selectedItemType = null;
        this.selectedGroup = group;
        this._modelWidgetService.selectedGroupGuid(group ? group.guid : null);
        this._$rootScope.$broadcast(groupSelected, group, true);
    }

    private computeMatricesFeedback(promise: angular.IPromise<{ [name: string]: string }>) {
        return promise
            .then(data => {
                const keys = Object.keys(data);
                if (keys.length > 0) {
                    keys.forEach(k => {
                        this._loggerService.logWarning(`Occurrence matrix of group ${k} cannot be updated.`);
                    });
                } else {
                    this._loggerService.logSuccess('All occurrence matrices updated');
                }
                return this.refreshTree(this.getGroupGuid());
            })
            .catch(reason => {
                this._loggerService.logError(`Update failed : ${reason}`);
            })
            .finally(() => {
                this.isLoading = false;
            });
    }

    private refreshStore(store: Store, isAsync: boolean) {
        if (isAsync) {
            const {
                alarms,
                approvalStrategies,
                groups,
                tags,
                templates,
                valueViewModels
            } = store;
            this.isAsyncStoreLoading = (alarms.isLoading
                || groups.isLoading
                || tags.isLoading
                || valueViewModels.isLoading);
            this.tags = _.values(tags.dictionary);
            this.groupsDictionary = groups.dictionary;
            this.viewModelsDictionary = valueViewModels.dictionary;
            if (this.selectedIdentifier && this.viewModelsDictionary) {
                this.selectedVM = this.selectedIdentifier ? this.viewModelsDictionary[this.selectedIdentifier.guid] : undefined;
            }
            if (!this.isAsyncStoreLoading) {
                const promise = this._$q.all([
                    this._groupService.getSteps(),
                    this._templateService.countAllChanges()
                ])
                    .then(data => {
                        const groupGuid = this.getGroupGuid();
                        const items = this._groupService.convertToTree(_.values(groups.dictionary));
                        const dto = this._modelStructureService
                            .buildTree(
                                items,
                                _.values(templates.dictionary),
                                _.values(approvalStrategies.dictionary),
                                data[0],
                                data[1]);
                        return { dto, groupGuid };
                    });
                return this.refreshTreeFromPromise(promise);
            }
        } else {
            const {
                approvalStrategies,
                connectionPoints,
                digitalSets,
                states,
                templates,
                users,
                validationPageConfigurations
            } = store;
            this.approvalStrategiesDictionary = approvalStrategies.dictionary;
            this.connectionPoints = _.values(connectionPoints.dictionary);
            this.digitalSets = _.values(digitalSets.dictionary);
            this.statesDictionary = states.dictionary;
            this.templates = Object.values(templates.dictionary);
            this.templatesDictionary = templates.dictionary;
            this.userViewModels = users.dictionary;
            this.validationPageConfigurationsDictionary = validationPageConfigurations.dictionary;

            const validationPagesConfigs = _.values<ValidationPageConfiguration>(validationPageConfigurations.dictionary);
            this.validationPageConfigurations = this.rebuildValidationPagesDictionary(validationPagesConfigs);

            if (connectionPoints.dictionary) {
                this.connectionPointsDictionary = {};
                const allConnectionPointTypes = connectionPointTypes.reduce((map, obj) => {
                    map[obj.key] = this._$translate.instant(obj.value);
                    return map;
                }, {});
                Object.keys(connectionPoints.dictionary).forEach(guid => {
                    this.connectionPointsDictionary[guid] = Object.assign({}, connectionPoints.dictionary[guid]) as ConnectionPointViewModel;
                    this.connectionPointsDictionary[guid].connectionPointType = allConnectionPointTypes[this.connectionPointsDictionary[guid].type];
                });
            }
            this.isStoreLoaded = true;
        }
    }

    private checkCreateChildApplicationAllowed(): void {
        this.appConfig = this._currentApplicationService
            .getApplicationConfiguration();
        this.isCreateChildApplicationAllowed = this.appConfig.isChildApplicationCreationAllowed;
        if (!this.isCreateChildApplicationAllowed) {
            this.hiddenStates = ['childApplications'];
        }
    }

    private canSelectItem(): boolean {
        return !this.formController || this.formController.$pristine;
    }

    private rebuildValidationPagesDictionary(data: ValidationPageConfiguration[]): { [name: string]: string } {
        const dic = {};
        data.forEach(d => dic[d.name] = d.referenceGroup);
        return dic;
    }

    private selectIdentifier(data: Identifier) {
        let identifier: Identifier = null;
        if (data && this.identifiers && this.identifiers.length > 0) {
            identifier = this.identifiers.filter(i => i && i.guid === data.guid)[0];
        }
        const selected = this._identifierStateService.selectedItem(identifier);
        if (selected === identifier) {
            this.selectedIdentifier = identifier;
            if (this.viewModelsDictionary) {
                this.selectedVM = identifier ? this.viewModelsDictionary[identifier.guid] : undefined;
            }
            if (identifier) {
                this.selectedItemType = 1;
            } else {
                this.selectedItemType = 0;
            }
        }
    }

    private refreshTree(groupGuid?: string) {
        const promise = this._modelStructureService
            .getTree()
            .then(dto => {
                return { dto, groupGuid };
            });
        return this.refreshTreeFromPromise(promise);
    }

    private refreshTreeFromPromise(promise: angular.IPromise<{ dto: GroupTreeViewModel[], groupGuid?: string }>) {
        this.isLoading = true;
        this.loadingMessage = 'Refreshing tree';
        return promise
            .then(data => {
                const { dto, groupGuid } = data;
                this.groups = dto;
                this.identifiers = [].concat(...this.groups.map(g => g.identifierCollection));
                this.hasError = _.some(this.groups, g => g.name !== 'root' && !g.isMatrixUpToDate);
                this._$rootScope.$broadcast(treeUpdated);
                if (groupGuid) {
                    this.selectedGroup = _.find(this.groups, g => g.guid === groupGuid);
                } else {
                    this.selectedGroup = null;
                }
            })
            .finally(() => {
                this.isLoading = false;
                this.loadingMessage = null;
            });
    }
}

export const wtModelsState: ComponentDefinition = {
    componentId: 'wtModelsState',
    controller: ModelsStateController,
    template: require('./modelsState.component.html'),
    controllerAs: 'vm',
    bindings: {
        sharedState: '<'
    }
};